import { useState } from 'react'
import html2pdf from 'html2pdf.js'
import JSZip from 'jszip'
import { supabase } from '../lib/supabase'
import { buildReportData } from '../lib/reportData'
import { buildFleetReportHtml, buildOwnerReportsHtml, approvalsByVessel, maintDoneByVessel, monthLabel, PDF_OPT } from '../lib/fleetReport'
import { waitingTotalText } from '../lib/approvalsCore'

// "Fleet report - October" on the Fleet page (fleet-2): one PDF = a fleet summary page + one page per vessel, or the owner report of
// every vessel in one zip (one PDF each, to send to each owner). Every vessel's figures come from buildReportData (the Reports page);
// readiness and the compliance items come from the Fleet page (health / items), so the PDF shows what the screen shows.
const thisMonth = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') }
const nextMonth = m => { const [y, mm] = m.split('-').map(Number); return mm === 12 ? (y + 1) + '-01' : y + '-' + String(mm + 1).padStart(2, '0') }
const safe = s => String(s || '').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '')
const q = p => Promise.resolve(p).then(r => (r && !r.error && r.data) || [], () => [])

// everything the PDFs need, per vessel (exported for the screenshot script / tests through the Fleet page only)
async function gather({ fleet, month, health, itemsFor, onStep }) {
  const ids = fleet.map(v => v.id), from = month + '-01', to = nextMonth(month) + '-01'
  const [poEvents, apaEvents, waiting, logs] = await Promise.all([
    q(supabase.from('purchase_order_events').select('client_id,action,at').in('client_id', ids).in('action', ['approval_added', 'rejected']).gte('at', from).lt('at', to).limit(5000)),
    q(supabase.from('apa_events').select('client_id,action,at').in('client_id', ids).in('action', ['closed', 'close_rejected']).gte('at', from).lt('at', to).limit(5000)),
    q(supabase.rpc('hc_my_approvals')),
    q(supabase.from('maintenance_log').select('client_id,date_completed').in('client_id', ids).like('date_completed', month + '%').limit(5000)),
  ])
  const appr = approvalsByVessel({ poEvents, apaEvents, waiting, month })
  const done = maintDoneByVessel(logs, month)
  const out = []
  for (const [i, v] of fleet.entries()) {
    onStep(v.vessel_name, i + 1, fleet.length)
    const report = await buildReportData({ config: { id: v.id, vessel_name: v.vessel_name, flag: v.flag || '', imo: v.imo || '' }, month })
    const a = appr[v.id] || { approved: 0, rejected: 0, apaClosed: 0, apaRejected: 0, handled: 0, waiting: 0, waitingRows: [] }
    out.push({ id: v.id, name: v.vessel_name, health: health[v.id] || null, report, items: itemsFor(v.id), approvals: { ...a, waitingText: waitingTotalText(a.waitingRows) }, maintDone: done[v.id] || 0 })
  }
  return out
}
async function pdfOf(html, filename, asBlob) {
  const el = document.createElement('div')
  el.style.position = 'fixed'; el.style.left = '-10000px'; el.style.top = '0'
  el.innerHTML = html
  document.body.appendChild(el)
  try { const w = html2pdf().set({ ...PDF_OPT, filename }).from(el.firstElementChild); return asBlob ? await w.outputPdf('blob') : await w.save() }
  finally { document.body.removeChild(el) }
}
function download(blob, name) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

export default function FleetReportButton({ fleet, company, health, itemsFor, ready, palette: C }) {
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(thisMonth)
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState('')
  const generated = () => new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  async function run(kind) {
    setBusy(kind); setMsg('')
    try {
      const vessels = await gather({ fleet, month, health, itemsFor, onStep: (name, i, n) => setMsg('Reading ' + name + ' (' + i + ' of ' + n + ')…') })
      if (kind === 'fleet') {
        setMsg('Making the PDF…')
        await pdfOf(buildFleetReportHtml({ company, month, vessels, generated: generated() }), safe(company || 'Fleet') + '_fleet_report_' + month + '.pdf')
        setMsg('Fleet report saved: 1 summary page + ' + vessels.length + ' vessel pages.')
      } else {
        const zip = new JSZip()
        for (const [i, o] of buildOwnerReportsHtml({ company, month, vessels, generated: generated() }).entries()) {
          setMsg('Owner report ' + (i + 1) + ' of ' + vessels.length + ': ' + o.name + '…')
          zip.file(safe(o.name) + '_owner_report_' + month + '.pdf', await pdfOf(o.html, safe(o.name) + '.pdf', true))
        }
        download(await zip.generateAsync({ type: 'blob' }), safe(company || 'Fleet') + '_owner_reports_' + month + '.zip')
        setMsg('Owner reports saved: ' + vessels.length + ' PDFs in one zip, one per vessel.')
      }
    } catch (e) { setMsg('Could not make the report: ' + (e.message || e)) } finally { setBusy('') }
  }

  const btn = { display: 'block', width: '100%', textAlign: 'left', background: 'rgba(59,123,255,.1)', border: '1px solid ' + C.line, color: C.text, borderRadius: 10, padding: '11px 13px', fontWeight: 600, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', marginTop: 8 }
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button className="hcf-brief" data-fleet-report onClick={() => setOpen(o => !o)} disabled={!fleet.length} title="One PDF: fleet summary + one page per vessel; or the owner reports of every vessel">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M13.5 3.5H7a2 2 0 00-2 2v13a2 2 0 002 2h10a2 2 0 002-2V9z" /><path d="M13.5 3.5V9H19M9 13h6M9 16.5h6" /></svg>
        Fleet report
      </button>
      {open && (
        <div data-report-panel style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 30, width: 'min(340px, calc(100vw - 32px))', background: C.card, border: '1px solid ' + C.line, borderRadius: 14, padding: 14, boxShadow: '0 14px 40px rgba(0,0,0,.45)', color: C.text }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <b style={{ fontSize: 14 }}>Monthly report</b>
            <input type="month" value={month} max={thisMonth()} onChange={e => e.target.value && setMonth(e.target.value)} disabled={!!busy}
              style={{ marginLeft: 'auto', background: 'var(--bg)', color: C.text, border: '1px solid ' + C.line, borderRadius: 8, padding: '6px 8px', fontWeight: 600, fontSize: 12.5, fontFamily: 'inherit' }} />
          </div>
          <button style={btn} data-run="fleet" disabled={!!busy} onClick={() => run('fleet')}>
            Fleet report &middot; {monthLabel(month)} (PDF)
            <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 500, marginTop: 3 }}>Summary page (readiness worst first, approvals, due 30 / 60 / 90 d, maintenance, drills, spend vs budget) + one page per vessel</div>
          </button>
          <button style={btn} data-run="owner" disabled={!!busy} onClick={() => run('owner')}>
            Owner reports for all {fleet.length} vessels (.zip)
            <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 500, marginTop: 3 }}>One owner report PDF per vessel, ready to send to each owner</div>
          </button>
          {!ready && !busy && <div style={{ fontSize: 11.5, color: C.amber, marginTop: 9 }}>Still checking the vessels: readiness shows once every vessel is read.</div>}
          {msg && <div role="status" style={{ fontSize: 12, color: /Could not/.test(msg) ? C.red : C.muted, marginTop: 9, lineHeight: 1.4 }}>{msg}</div>}
        </div>
      )}
    </span>
  )
}
